import _ from 'lodash';

const cache = {};

/**
 * Class to simplify values needed to resolve a route
 */
export class ApiResourcify {
	/**
	 * Return a resolve function that will fetch a record if $stateParams.id is set
	 * otherwise create and use a new record
	 * @param {String} endpoint  The GET/POST endpoint url such as "/post"
	 * @param {Object} [options]
	 * @property {Object} [data]  Data to send to GET or POST
	 * @property {String} [paramName="id"]  The name of the ID field
	 * @returns {Function}
	 */
	static addEdit(endpoint, options = {}) {
		return function ($http, $stateParams) {
			'ngInject';
			let url = ApiResourcify._getPrefix(endpoint) + endpoint;
			let data = options.data;
			let paramName = options.paramName || 'id';
			if (/\d+/.test($stateParams[paramName])) {
				url += '/' + $stateParams[paramName];
				return $http({ method: 'GET', url, data }).then(response => response.data);
			} else {
				return $http({ method: 'POST', url, data }).then(response => {
					return $http.get(response.headers('Location'));
				});
			}
		};
	}

	/**
	 * Shortcut to run endpoint() with an "Authorization: Public" header
	 * @param {String} endpoint  The public GET endpoint url such as "/feeds/logos"
	 * @param {Object} [options]
	 * @property {Object} [headers]  Additional headers to send with the request
	 * @property {Function} [withResponse]  A function to use to transform the response
	 *   instead of just fetching the data
	 * @property {String} [cacheFor]  A time period until which to return the last response
	 *   instead of making a new request. Examples "5 minutes", "30 seconds", 2 hours", etc.
	 * @returns {Function}  The function to pass to the router as a resolve
	 */
	static public(endpoint, options = {}) {
		options.headers = options.headers || {};
		options.headers.Authorization = 'Public';
		return this.endpoint(endpoint, options);
	}

	/**
	 * Fetch data from the given endpoint
	 * @param {String} endpoint  The GET endpoint url such as "/tags"
	 * @param {Object} [options]
	 * @property {Object} [headers]  Additional headers to send with the request
	 * @property {Function} [withResponse]  A function to use to transform the response
	 *   instead of just fetching the data
	 * @property {String} [cacheFor]  Milliseconds until which to cache the response for this url
	 * @returns {Function}  The function to pass to the router as a resolve
	 */
	static endpoint(endpoint, options = {}) {
		return ($http, $stateParams) => {
			'ngInject';
			let url = ApiResourcify._getPrefix(endpoint) + endpoint.replace(/:([\w_]+)/g, ($0, $1) => $stateParams[$1] || '');
			let headers = options.headers || {};
			var request = {
				method: 'GET',
				url,
				headers,
			};
			if (options.cacheFor && cache[url]) {
				return cache[url];
			}
			let promise = $http(request);
			if (options.withResponse) {
				promise = promise.then(options.withResponse);
			} else {
				promise = promise.then(response => response.data);
			}
			promise.then(data => {
				if (_.size(data) === 0) {
					cache[url] = false;
				}
				return data;
			});
			if (options.cacheFor) {
				cache[url] = promise;
				const clearCache = () => {
					setTimeout(() => (cache[url] = false), options.cacheFor);
				};
				promise.then(clearCache, clearCache);
			}
			return promise;
		};
	}

	/**
	 * Create a new record and return it
	 * @param {String} endpoint  The POST endpoint such as "/tags"
	 * @param {Object} [withValues]  The values with which to create the record
	 * @returns {Function}
	 */
	static create(endpoint, withValues = {}) {
		return function ($http, $stateParams) {
			'ngInject';
			let url = ApiResourcify._getPrefix(endpoint) + endpoint.replace(/:([\w_]+)/g, ($0, $1) => $stateParams[$1] || '');
			return $http.post(url, withValues).then(response => response.data);
		};
	}

	/**
	 * Clear the cache for one or more URL endpoint
	 * @param {String} [url]  The endpoint such as "/tags"; if omitted, clear cache for all URLs
	 * @returns {ApiResourcify}
	 */
	static clearCache(url) {
		if (url) {
			cache[url] = false;
		} else {
			_.forOwn(cache, (val, key) => {
				cache[key] = false;
			});
		}
		return this;
	}

	/**
	 * Given the endpoint URL, add the appropriate prefix (none, /api, or /api/v2)
	 * @param {String} endpoint  The requested URL fragment such as "/posts/123" or "/v3/posts/search"
	 * @returns {String}
	 * @private
	 */
	static _getPrefix(endpoint) {
		if (endpoint.match(/^(https?)?:\/\//i)) {
			// full url
			return '';
		}
		if (endpoint.match(/\/v\d+\//)) {
			// already has /v2/ or /v3/ at the front
			return '/api';
		}
		// bare url
		return '/api/v2';
	}
}
